/**
 * Referral panel (1.2): the checks a branch cannot run today, and the two
 * answers behind them.
 *
 *   Service area   — the zip is inside the branch and open to referrals.
 *   SOC inside 48h — a real slot on a real SOC-capable clinician inside 48 hours
 *                    of the expected discharge, from `holdSlotFor`.
 *   Capacity       — one line per ordered discipline, tightest first: next
 *                    week's load **with this referral's expected plan added**,
 *                    against the target, and the figure without it.
 *
 * There is no authorization row. Every check here is computed from the schedule;
 * authorization is received from the EMR, and the payer it belongs to is a fact
 * in the panel header.
 *
 * The capacity number is the one the beat turns on. The read is planned plus
 * everything reserved — held for SOC, pending auth, awaiting plan; an undecided
 * referral is in none of those, so the panel adds its own expected plan as a
 * **last part** by pricing a prospective hold into the same selector the heat
 * map reads. Changing the template therefore moves the "with"
 * figure and cannot move the "without" one, and on Accept the prospective hold
 * becomes the real one and the two figures collapse.
 *
 * There is no recommendation and no headline: a failed check is a reason on the
 * screen, not a verdict, and nothing blocks. The one button names what it will
 * hold and is always enabled.
 */
import type { DateKey, DemoState, Discipline, DisciplineGroup, Hold, IsoString, PlanVisit, Referral, RosterEntry } from "@caredna-marlin/utils/homeHealthDemo/state";
import { committedPoints, selectTerritoryDisciplineWeek } from "./capacity";
import { describeReserved, reservedTotal, type ReservedByReason } from "./reserved";
import { addDaysToKey, dateKeyAt, dateKeyRange, dateKeyStart, formatInBranchZone, selectDemoNow, toDateKey, weekStartKey } from "@caredna-marlin/utils/homeHealthDemo/engine/clock";
import { type TimeSentence } from "@caredna-marlin/utils/homeHealthDemo/engine/timeText";
import { dayBounds, overlapMinutes, providerIdFor } from "./clinicianDay";
import { expectedPlanFor, describePlanPattern, type ExpectedPlan } from "./expectedPlan";
import { buildTail, createHold, holdWindowEnd, socDueByIso, socDueWindow, type HoldSlot } from "./holds";
import { effectiveVisitResources } from "./effectiveCalendar";
import { planVisitLayers } from "./utilization";
import { disciplineGroupFor, hasTraits, isInRamp, isSocCapable, primaryTerritory, rosterEntries, socPathDisciplines } from "./roster";
import { socSlotsOpenWithin } from "./socLane";
import { estimateDriveMinutes } from "./calloutRanking";
import { territoryForZip, zipStatus } from "@caredna-marlin/utils/homeHealthDemo/engine/territory";
import { pointsFor } from "@caredna-marlin/utils/homeHealthDemo/engine/visitTypes";

/** A real engine option (from `scheduleOption`) normalized for the panel. */
export interface CandidateSlot {
  providerId: number;
  startIso: IsoString;
  endIso: IsoString;
  arrivalWindowStartIso?: IsoString;
  arrivalWindowEndIso?: IsoString;
  /** The drive to the patient. */
  originDriveMinutes: number;
  /**
   * The drive away again, to whatever the clinician does next. The product shows
   * **the larger of the two** wherever it prints a drive for an option — the
   * scheduler screen's detail panel and both admission panels all do
   * `Math.max(originDrive, destinationDrive)` — because the cost of taking this
   * slot is the longer leg it forces, not the one in.
   */
  destinationDriveMinutes?: number;
  totalScore: number;
  rankCategory?: string;
}

/**
 * What the product's own scheduler says about a clinician, from
 * `scheduleProvidersList` — the query behind the filter list on its scheduling
 * screen. It is the reason half of the same answer `scheduleOption` gives: one
 * says who could have taken this, the other says who can.
 */
export interface ProviderEligibility {
  hasServiceLine: boolean;
  hasProviderType: boolean;
  hasStateLicense: boolean;
  hasNearbyShifts: boolean;
  hasNonOverflowShifts: boolean;
}

export interface ReferralCheckInputs {
  candidateSlots?: CandidateSlot[];
  /** By provider id, as `scheduleProvidersList` returned it. */
  providerEligibility?: Record<number, ProviderEligibility>;
  /**
   * The caller asked `scheduleOption` and this is its answer, even when the
   * answer is none. Without it an empty list is indistinguishable from a caller
   * that never asked, and the panel would fall back to the local search — the
   * one behaviour this is meant to remove.
   */
  slotsAreAuthoritative?: boolean;
  /**
   * The question is out and there is no answer yet. Nothing may be concluded
   * from an empty list while this is true: the check said **no SOC-capable
   * clinician** in red for the second the query was in flight, then turned green
   * when the answer landed (Pete, 2026-09-24). A question nobody has answered is
   * not a failure.
   */
  slotsPending?: boolean;
  wallNow?: Date;
}

/**
 * Why a SOC-capable clinician is not an option. Three readings and no others
 * (Pete, 2026-09-22): the drawer is a glance, not a calendar.
 */
export const EXCLUSION = {
  notWorking: "Not working",
  noCapacity: "No capacity",
  orientation: "In orientation, No capacity",
} as const;

export type CheckKey = "serviceArea" | "socWithin48h" | "capacity";
export type CheckStatus = "pass" | "caution" | "warn" | "fail" | "info";

export interface Check {
  key: CheckKey;
  label: string;
  status: CheckStatus;
  /** Prose, with a time token wherever a clock time belongs. The view phrases it. */
  detail: TimeSentence;
  note?: string;
}

/**
 * Where a clinician belongs on the panel.
 *
 *   option — fits: SOC-capable, holds every trait the case needs, and has a slot
 *            inside 48 hours. These are SOC Options.
 *   other  — could have, and did not, for a reason about the calendar rather than
 *            the clinician: in orientation, not working inside the window, no
 *            declared day, at the ceiling. These sit behind the disclosure.
 *   hidden — a different discipline, or missing a trait the case needs. Not
 *            listed anywhere: the wound-VAC requirement is why nobody else is on
 *            the screen at all.
 */
export type CandidateListing = "option" | "other" | "hidden";

export interface SocCandidate {
  handle: string;
  displayName: string;
  discipline: Discipline;
  traits: string[];
  eligible: boolean;
  listing: CandidateListing;
  exclusion?: string;
  providerId?: number;
  slotDate?: DateKey;
  slotTime?: string;
  partOfDay?: "morning" | "afternoon";
  driveMinutes?: number;
  driveIsEstimate: boolean;
  score?: number;
  /**
   * How good the product's own scheduler thinks this slot is — `high`, `medium`
   * or `low`, its word not ours. Absent when the slot came from this file's
   * search, which ranks nothing.
   */
  rankCategory?: string;
  /**
   * The days inside the window this clinician could **not** take, in words. An
   * option carries it too — Dana fits Friday and her Thursday is still worth
   * saying — which is why the disclosure lists options with a note beside the
   * clinicians who are not options at all.
   */
  windowNote?: string;
}

export interface ExpectedPlanRow {
  discipline: Discipline;
  requiredTraits: string[];
  pattern: string;
  /** The pattern in words: "SOC, 2 a week × 3 wks, then every other week × 6 wks". */
  patternWords: string;
  pointsPerWeek: number;
}

/**
 * One line of the Capacity check: next week's committed load for a discipline
 * group in the referral's territory, with this referral's expected plan added
 * and without it.
 */
export interface CapacityRow {
  territory: string;
  group: DisciplineGroup;
  weekStart: DateKey;
  target: number;
  /** Next week with this referral's expected plan added. */
  withThis: number;
  /** Next week without it. The template dropdown never moves this. */
  without: number;
  /** The solid layer: planned visits for the territory's patients. */
  planned: number;
  /** Everything reserved — the hatched layer — as one number. */
  reserved: number;
  /** The same number split by reason, for the hover. */
  reservedByReason: ReservedByReason;
  /** The last part of the hover: what this referral's expected plan adds. */
  thisReferral: number;
  over: boolean;
}

/** "Is there an SOC slot inside 48 hours": a real slot on a real clinician. */
export interface SocSlotAnswer {
  handle?: string;
  displayName?: string;
  date: DateKey;
  windowStart: string;
  windowEnd: string;
  partOfDay: "morning" | "afternoon";
}

/** Summary colour over the checks. No words: the checks carry the reasons. */
export type CheckSummary = "clear" | "marginal" | "blocked";

export interface ReferralChecks {
  referralId: string;
  patientRef: string;
  receivedAtIso: IsoString;
  expectedDischarge: DateKey;
  socDueByIso: IsoString;
  zipStatus: ReturnType<typeof zipStatus>;
  territory?: string;
  socPath: Discipline[];
  /** Everything a start of care needs: SOC-capability plus the case's own traits. */
  requiredTraits: string[];
  /** The case's own traits — what the referral asks for beyond being SOC-capable. Safe to render. */
  caseTraits: string[];
  /** The one expected-plan selector, shared with the hold and the estimate layer. */
  plan: ExpectedPlan;
  answers: { socSlot?: SocSlotAnswer };
  checks: Check[];
  capacity: CapacityRow[];
  summary: CheckSummary;
  candidates: SocCandidate[];
  /** Clinicians who fit, best first: the SOC-inside-48h slot, then drive. */
  socOptions: SocCandidate[];
  /** The disclosure: clinicians who could have taken it and did not, one reason each. */
  otherClinicians: SocCandidate[];
  /**
   * What the disclosure actually lists: everyone SOC-capable with the case's
   * traits whose window is worth explaining — the clinicians who are not options
   * at all, and the options that could only take part of the window.
   */
  disclosure: SocCandidate[];
  expectedPlan: ExpectedPlanRow[];
}

/** How a discipline group reads on the capacity line: "North SN next week". */
export const DISCIPLINE_GROUP_SHORT: Record<DisciplineGroup, string> = { SN: "SN", PT: "PT", OT: "OT", SLP: "SLP", MSW: "MSW", HHA: "HHA" };

/**
 * The SOC clock on screen is a **day**, never a time: "SOC by Sat Sep 26". The
 * 48-hour instant is midnight after the second day, and a wall-clock rendering of
 * it ("Sat 00:00") reads as an appointment nobody made.
 */
function socByDay(iso: IsoString, tz: string): string {
  return formatInBranchZone(new Date(iso), tz, "EEE MMM d");
}

/**
 * The days a hold may land on: the expected-discharge window (the day of
 * discharge and the day after), never in the past. The patient is still
 * inpatient before that, so today is not a candidate.
 */
export function holdWindowDays(referral: Pick<Referral, "expectedDischarge">, today: DateKey, dueWindow?: { start: DateKey; end: DateKey }): DateKey[] {
  const window = dueWindow ?? socDueWindow(referral.expectedDischarge);
  const start = window.start < today ? today : window.start;
  return start > window.end ? [] : dateKeyRange(start, window.end);
}

/**
 * Why a clinician's day could not take the thing being placed. The referral
 * panel's disclosure phrases these; nothing else reads them.
 *
 *   notWorking — no confirmed shift. She is not there at all that day.
 *   reserved   — the free time is spoken for by a reservation, not by a visit:
 *                a start of care held for another admission is the case 1.2
 *                names out loud.
 *   noSpan     — she is working and the day is simply full of visits.
 */
export type NoFitReason = "notWorking" | "reserved" | "noSpan";

/** One clinician-day's answer: a start time, or the reason there is none. */
export interface DayFit {
  time?: string;
  reason?: NoFitReason;
  /** On a `reserved` no-fit, the hour the reservation in the way begins — so the panel can say "Thursday morning". */
  blockedAt?: string;
}

export interface OpenSpanQuery {
  /** Never start before this wall time. */
  notBefore?: string;
  /**
   * Minutes of free time the span has to hold. A **reservation** asks for the
   * visit plus the drive allowance, because that is what it is promising to
   * keep. A **conversion** asks for the visit alone: the reservation it is
   * replacing already reserved the drive, and re-charging for it would refuse
   * the very slot the hold was placed in.
   */
  needMinutes?: number;
  /** Reservations that are not obstacles to themselves — the one being placed, re-held or converted. */
  excludeHoldIds?: string[];
  /** Plan visits, likewise: a recertification re-placing itself does not collide with itself. */
  excludePlanVisitIds?: string[];
}

/**
 * There is no hierarchy between reservations (Pete, 2026-09-21). A real visit
 * displaces a reservation; reservations never displace each other, so the first
 * one placed keeps its span and every later search goes around it.
 */

interface Busy {
  start: number;
  end: number;
  /** A promise rather than a fact — the one obstacle worth naming out loud. */
  reservation: boolean;
}

/**
 * The first start on a working day inside the window, on the first day that has
 * one. Days are tried in order, so the answer is the earliest slot inside the
 * window — never the best one, because this is not a ranking.
 *
 * The no-fit reason is the **worst** the window produced, in the order a person
 * would say it: a day that is reserved beats a day that is merely full, which
 * beats a day she is not working. That is what makes 1.2's disclosure read
 * "Thursday morning already held for another admission and Friday full" rather
 * than "not working inside the 48h window", which was never true of a clinician
 * who is on shift both days.
 */
export function firstOpenSlot(state: DemoState, entry: RosterEntry, days: DateKey[], q: OpenSpanQuery = {}): { slot?: { date: DateKey; time: string }; reason?: NoFitReason } {
  const providerId = providerIdFor(state, entry);
  if (providerId === undefined) return { reason: "notWorking" };
  const rank: Record<NoFitReason, number> = { notWorking: 0, noSpan: 1, reserved: 2 };
  let worst: NoFitReason | undefined;
  for (const day of days) {
    const fit = openSpanOnDay(state, providerId, day, q);
    if (fit.time) return { slot: { date: day, time: fit.time } };
    if (fit.reason && (!worst || rank[fit.reason] > rank[worst])) worst = fit.reason;
  }
  return { reason: worst ?? "notWorking" };
}

/**
 * Every day inside the window that could not take it, in the branch's own words:
 * "Thursday morning already held for another admission · Friday full". A day she
 * could take is left out, so an option's note reads as the days it could not.
 *
 * The screen never quotes the span in hours. The search sizes on the SOC's
 * duration plus the drive allowance, both configuration, and a label carrying
 * one of those numbers would go stale the moment either moved.
 */
export function describeWindowDays(state: DemoState, entry: RosterEntry, days: DateKey[], q: OpenSpanQuery = {}): string | undefined {
  const tz = state.config.branch.timeZone;
  const providerId = providerIdFor(state, entry);
  if (providerId === undefined) return undefined;
  const parts: string[] = [];
  for (const day of days) {
    const fit = openSpanOnDay(state, providerId, day, q);
    if (fit.time) continue;
    const name = formatInBranchZone(dateKeyStart(day, tz), tz, "EEEE");
    if (fit.reason === "reserved") parts.push(`${name} ${fit.blockedAt ? `${partOfDayFor(fit.blockedAt)} ` : ""}already held for another admission`);
    else if (fit.reason === "noSpan") parts.push(`${name} full`);
    else parts.push(`not working ${name}`);
  }
  return parts.length ? parts.join(" · ") : undefined;
}

/**
 * Free time on one clinician-day, and nothing else.
 *
 * A reservation is placed into free time when it is created or re-held, never
 * on top of a visit, a block or another reservation (Pete, 2026-09-20). So the
 * search walks the day's obstacles in order — blocks, real visits, and the
 * reservations already standing on it — and returns the first gap wide enough.
 * It never returns a colliding start: no room inside the window is a no-fit,
 * which the caller turns into a worklist row rather than a double booking.
 *
 * Real visits come through `effectiveVisitResources`, so a visit the clinician
 * has just moved is where she put it rather than where the server still has it.
 * Days past the seeded horizon carry no server visits, and the virtual census
 * that fills the branch read there has no per-visit hours to collide with; a
 * window that far out is outside every act, and the search reads such a day as
 * open.
 */
/** `"HH:mm"` plus a run of minutes, as `"HH:mm"`. Local so this module keeps no dependency on the placement pass. */
function minutesAfter(hhmm: string, minutes: number): string {
  const [h, m] = hhmm.split(":").map(Number);
  const total = h * 60 + m + minutes;
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
}

export function openSpanOnDay(state: DemoState, providerId: number, day: DateKey, q: OpenSpanQuery = {}): DayFit {
  const tz = state.config.branch.timeZone;
  const needMs = (q.needMinutes ?? state.config.socSlot.durationMinutes + state.config.socSlot.driveMinutes) * 60_000;
  const { start: dayStart, end: dayEnd } = dayBounds(day, tz);
  const shift = state.server.shifts.find((s) => s.providerId === providerId && s.status === "confirmed" && !s.overflow && overlapMinutes(s.startIso, s.endIso, dayStart, dayEnd) > 0);
  if (!shift) return { reason: "notWorking" };

  const busy: Busy[] = [];
  const at = (hhmm: string) => dateKeyAt(day, hhmm, tz).getTime();
  for (const b of state.server.blocks) {
    if (b.canceled || b.providerId !== providerId || overlapMinutes(b.startIso, b.endIso, dayStart, dayEnd) === 0) continue;
    busy.push({ start: new Date(b.startIso).getTime(), end: new Date(b.endIso).getTime(), reservation: false });
  }
  /**
   * **A visit the reset has disowned is not in the way** (Pete, 2026-09-25).
   * 1.4 converts Okafor's hold into a real start of care on Marcus's morning.
   * Reset this browser puts the hold back; the visit stays on the server, because the
   * demo does not delete what it wrote. The hold then collided with its own
   * converted self — the only 150-minute span on that day was the one it had
   * just been turned into — and the row read *Needs a time*, which is how a
   * rehearsed 1.4 broke the next run of 1.4.
   *
   * `meta.excludedVisitIds`, **not** `excludedVisitIdsFrom`: the reset is what
   * fills that list, so a conversion stays solid for the rest of a live run and
   * nothing else is dealt its span. Only after the demo has disowned it does the
   * hour come back.
   */
  const disowned = new Set(state.meta.excludedVisitIds);
  for (const vr of effectiveVisitResources(state)) {
    if (vr.providerId !== providerId || vr.eventStatus === "canceled" || overlapMinutes(vr.startIso, vr.endIso, dayStart, dayEnd) === 0) continue;
    if (disowned.has(vr.visitId)) continue;
    busy.push({ start: new Date(vr.startIso).getTime(), end: new Date(vr.endIso).getTime(), reservation: false });
  }
  // Reservations are keyed by handle, the day's visits by provider id; one lookup joins them.
  const handle = rosterEntries(state).find((e) => providerIdFor(state, e) === providerId)?.handle;
  const excludedHolds = new Set(q.excludeHoldIds ?? []);
  for (const hold of Object.values(state.holds)) {
    if (hold.status !== "held" || hold.date !== day || excludedHolds.has(hold.id)) continue;
    if (handle === undefined || hold.providerHandle !== handle) continue;
    busy.push({ start: at(hold.windowStart), end: at(hold.windowEnd), reservation: true });
  }
  const excludedPlanVisits = new Set(q.excludePlanVisitIds ?? []);
  const layers = planVisitLayers(state);
  for (const visit of Object.values(state.planVisits)) {
    if (visit.targetDate !== day || visit.windowStart === undefined) continue;
    if (layers[visit.id] === undefined || excludedPlanVisits.has(visit.id)) continue;
    if (handle === undefined || visit.providerHandle !== handle) continue;
    // **An hour with no end is still an hour.** A reservation staged with a
    // `windowStart` and no `windowEnd` used to be invisible here, so the next
    // search dealt its span to somebody else and two promises ended up on it.
    // A reservation with a start is claiming the visit plus the drive allowance
    // from that start, which is what it is promising to keep.
    const end = visit.windowEnd ?? minutesAfter(visit.windowStart, state.config.visitTypes[visit.visitTypeKind].plannedMinutes + state.config.socSlot.driveMinutes);
    busy.push({ start: at(visit.windowStart), end: at(end), reservation: true });
  }
  busy.sort((a, b) => a.start - b.start);

  const floor = q.notBefore ? at(q.notBefore) : 0;
  const shiftEnd = new Date(shift.endIso).getTime();
  let cursor = Math.max(new Date(shift.startIso).getTime(), dayStart.getTime(), floor);
  // What pushed the cursor along. A **reservation** among them is the reason
  // worth saying out loud — "already held for another admission" is a fact about
  // this branch's own decisions, where "full" is only a fact about the day — so
  // the first reservation in the chain wins over the visits after it.
  let reservationInTheWay: Busy | undefined;
  for (const b of busy) {
    if (b.start >= cursor + needMs) break;
    if (b.end > cursor) {
      cursor = b.end;
      if (b.reservation && !reservationInTheWay) reservationInTheWay = b;
    }
  }
  if (cursor + needMs > shiftEnd) {
    return reservationInTheWay ? { reason: "reserved", blockedAt: formatInBranchZone(new Date(reservationInTheWay.start), tz, "HH:mm") } : { reason: "noSpan" };
  }
  return { time: formatInBranchZone(new Date(cursor), tz, "HH:mm") };
}

/**
 * The first start that clears the clinician's blocks, visits and reservations,
 * sized on the visit alone. Conversion reads it: a start of care never lands on
 * the documentation block when the welcome call's window opens earlier than the
 * clinician's day does.
 */
export function openStartOnDay(state: DemoState, providerId: number, day: DateKey, notBefore?: string, q: Omit<OpenSpanQuery, "notBefore" | "needMinutes"> = {}): string | undefined {
  return openSpanOnDay(state, providerId, day, { ...q, notBefore, needMinutes: state.config.visitTypes.SOC.plannedMinutes }).time;
}

/**
 * A referral's own reservations. The search skips them, because a hold being
 * re-held into a wider window would otherwise find itself standing in the way
 * and report no fit, and a hold being converted would refuse the very slot it
 * was placed in.
 */
export function ownHoldIds(state: Pick<DemoState, "holds">, referralId: string): string[] {
  return Object.values(state.holds)
    .filter((h) => h.referralId === referralId)
    .map((h) => h.id);
}

export function partOfDayFor(windowStart: string): "morning" | "afternoon" {
  return Number(windowStart.slice(0, 2)) < 12 ? "morning" : "afternoon";
}

/**
 * The prospective hold: what Accept would place, priced into the branch read so
 * the panel can show next week **with this referral** beside next week without
 * it. It is never dispatched and never persisted; it exists only to be handed to
 * `selectTerritoryDisciplineWeek`, which is the same selector the heat map reads.
 */
export function prospectiveHold(state: DemoState, referral: Referral, wallNow?: Date): Hold {
  const slot = holdSlotFor(state, referral, { wallNow }) ?? unassignedFallbackSlot(state, referral);
  const hold = createHold(referral, slot, state.config);
  return { ...hold, id: `hold-preview-${referral.id}` };
}

/**
 * With no fitting clinician the hold still lands — nothing blocks — and it lands
 * **unassigned on the last day the start of care may legally happen**: the day
 * after discharge. The 48-hour clock runs out at the start of the day after
 * that, so this is the last day a visit can be on. Dating it the day of
 * discharge instead would read as a promise to be there the day they get home,
 * which is not what an unfilled hold means.
 */
export function unassignedFallbackSlot(state: Pick<DemoState, "config">, referral: Referral): HoldSlot {
  const due = socDueWindow(referral.expectedDischarge);
  return { providerHandle: undefined, date: due.end, windowStart: "10:00", windowEnd: holdWindowEnd("10:00", state.config) };
}

/**
 * The Capacity check, one line per discipline group the expected plan covers.
 *
 * `without` is the branch read as it stands. `with` is the same read on a state
 * carrying the prospective hold, so the fourth part — this referral's own
 * expected plan — is a difference between two runs of one selector rather than a
 * number added by hand. Once the referral is accepted the hold is real, there is
 * nothing prospective left to add, and the two figures are the same number.
 */
export function capacityRowsFor(state: DemoState, referral: Referral, plan: ExpectedPlan, territory: string, weekStart: DateKey, preview?: Hold): CapacityRow[] {
  const withState: DemoState = preview ? { ...state, holds: { ...state.holds, [preview.id]: preview } } : state;
  const groups: DisciplineGroup[] = [];
  for (const order of plan.orders) {
    const group = disciplineGroupFor(order.discipline);
    if (!groups.includes(group)) groups.push(group);
  }
  const rows = groups.map((group): CapacityRow => {
    const base = selectTerritoryDisciplineWeek(state, territory, group, weekStart);
    const withCell = preview ? selectTerritoryDisciplineWeek(withState, territory, group, weekStart) : base;
    const without = committedPoints(base);
    const withThis = committedPoints(withCell);
    return {
      territory,
      group,
      weekStart: base.weekStart,
      target: base.target,
      withThis,
      without,
      planned: base.plotted,
      reserved: reservedTotal(base.reserved),
      reservedByReason: base.reserved,
      thisReferral: Math.round((withThis - without) * 4) / 4,
      over: base.target > 0 && withThis > base.target,
    };
  });
  // Tightest first: the discipline with the least room left, not the largest number.
  const pressure = (r: CapacityRow) => (r.target > 0 ? r.withThis / r.target : r.withThis > 0 ? Number.POSITIVE_INFINITY : 0);
  return rows.sort((a, b) => pressure(b) - pressure(a) || a.group.localeCompare(b.group));
}

export function selectReferralChecks(state: DemoState, referralId: string, inputs: ReferralCheckInputs = {}): ReferralChecks | undefined {
  const referral: Referral | undefined = state.referrals[referralId];
  if (!referral) return undefined;
  const patient = state.patients[referral.patientRef];
  const tz = state.config.branch.timeZone;
  const now = selectDemoNow(state, inputs.wallNow ?? new Date());
  const nowIso = now.toISOString();
  const today = toDateKey(now, tz);
  const socDue = socDueByIso(referral, tz);
  const zs = zipStatus(state.config.branch, patient?.zip, state.sandbox);
  const territory = territoryForZip(state.config.branch, patient?.zip);
  const plan = expectedPlanFor(state.config, referral);
  const orders = plan.orders;
  const orderDisciplines = referral.orders.length ? referral.orders.map((o) => o.discipline) : orders.map((o) => o.discipline);
  const socPath = socPathDisciplines(orderDisciplines);
  const socOrder = orders.find((o) => socPath.includes(o.discipline as never)) ?? referral.orders.find((o) => socPath.includes(o.discipline as never)) ?? referral.orders[0];
  const requiredTraits = Array.from(new Set([...state.config.visitTypes.SOC.requiredTraits, ...(socOrder?.requiredTraits ?? [])]));
  // What the case asks for beyond SOC-capability. The SOC trait itself is the
  // screen's word "SOC-capable" and is never rendered as a trait name.
  const caseTraits = requiredTraits.filter((t) => !state.config.visitTypes.SOC.requiredTraits.includes(t));
  const checks: Check[] = [];

  // 1. Hard check.
  const inArea = zs === "open";
  checks.push({
    key: "serviceArea",
    label: "Service area & zip",
    status: inArea ? "pass" : "fail",
    // A pass says the zip and the territory it lands in and stops (Pete,
    // 2026-09-22): "inside the branch service area and open" is the row reading
    // its own status back out loud. The two failing readings keep their sentence,
    // because there the reason is the whole of the content.
    detail: zs === "open" ? [patient?.zip, territory].filter(Boolean).join(" · ") : zs === "closed" ? `${patient?.zip} is closed to new referrals (branch intake list).` : `${patient?.zip ?? "no zip"} is outside the branch service area.`,
  });
  const base = { referralId, patientRef: referral.patientRef, receivedAtIso: referral.receivedAtIso, expectedDischarge: referral.expectedDischarge, socDueByIso: socDue, zipStatus: zs, territory, socPath, requiredTraits, caseTraits, plan };
  if (!inArea) {
    return { ...base, answers: {}, checks, capacity: [], candidates: [], socOptions: [], otherClinicians: [], disclosure: [], expectedPlan: [], summary: "blocked" };
  }

  // 2. SOC candidates inside the 48h window (the same numbers as the SOC lane).
  const days = holdWindowDays(referral, today);
  const search: OpenSpanQuery = { excludeHoldIds: ownHoldIds(state, referralId) };
  const lane = socSlotsOpenWithin(state, nowIso, socDue);
  /**
   * The caller put the question to `scheduleOption` and this is its reply — even
   * when the reply is none. Every reading below that used to come from this
   * file's own search comes from the scheduler instead, so the checks card and
   * the options table can never be counting different things.
   */
  const pending = inputs.slotsPending === true;
  // Pending counts as asked: the local search must not answer in the meantime,
  // or the panel shows its reading for a moment and the scheduler's after.
  const asked = pending || (inputs.candidateSlots?.length ?? 0) > 0 || inputs.slotsAreAuthoritative === true;
  const bySlotProvider = new Map<number, CandidateSlot>();
  for (const s of inputs.candidateSlots ?? []) {
    const prev = bySlotProvider.get(s.providerId);
    if (!prev || s.totalScore > prev.totalScore) bySlotProvider.set(s.providerId, s);
  }
  const candidates: SocCandidate[] = rosterEntries(state)
    .filter((e) => e.roleKind === "assessing")
    .map((entry): SocCandidate => {
      const providerId = providerIdFor(state, entry);
      const common = { handle: entry.handle, displayName: entry.displayName, discipline: entry.discipline, traits: entry.traits, providerId, driveIsEstimate: !bySlotProvider.has(providerId ?? -1) };
      if (!socPath.includes(entry.discipline as never)) return { ...common, eligible: false, listing: "hidden", exclusion: `${entry.discipline} does not open a ${socPath.join("/")}-led case` };
      // Orientation is read before capability, so a clinician still in ramp reads
      // as in orientation rather than as missing a trait they are days from holding.
      if (isInRamp(entry, today)) return { ...common, eligible: false, listing: "other", exclusion: EXCLUSION.orientation };
      if (!isSocCapable(entry, today)) return { ...common, eligible: false, listing: "hidden", exclusion: "Not SOC-capable" };
      if (!hasTraits(entry, requiredTraits)) {
        // The SOC trait is the screen's word "SOC-capable"; the case's own traits keep their names.
        const missing = requiredTraits.filter((t) => !entry.traits.includes(t)).map((t) => (caseTraits.includes(t) ? t : "SOC-capable"));
        return { ...common, eligible: false, listing: "hidden", exclusion: `Missing ${Array.from(new Set(missing)).join(", ")}` };
      }
      /**
       * **The product's scheduler decides whether there is a slot, when it has
       * been asked.** `inputs.candidateSlots` is what `scheduleOption` returned;
       * when the caller supplied any, a clinician is an option because the real
       * engine offered them one, not because this file's own search agrees.
       *
       * That is the whole point of asking it. This file's search cannot see a
       * clinician's day the way the scheduler does and — as 2026-09-23 proved on
       * stage — it counts the demo's own reservations as walls, so it answered
       * *No capacity* for Alvarez on a Thursday and Friday the scheduler would
       * have filled. There is deliberately **no fallback** between the two: a
       * caller that asked the service and got nothing shows nothing, rather than
       * quietly substituting a second opinion nobody can see.
       *
       * Callers that pass no slots at all — the specs, and anything that only
       * wants the local reading — keep the search they always had.
       */
      const offered = providerId !== undefined && bySlotProvider.has(providerId);
      const { slot, reason } = asked ? { slot: undefined, reason: "noSpan" as NoFitReason } : firstOpenSlot(state, entry, days, search);
      const windowNote = asked ? undefined : describeWindowDays(state, entry, days, search);
      if (asked ? !offered : !slot) {
        // Three words for three states (Pete, 2026-09-22). A per-diem with no
        // declared day and a full-timer who is off that week are both **not
        // working**, and every way a day can be spoken for — full, or already
        // held for another admission — is **no capacity**. The day-by-day reading
        // is still computed and still on the candidate as `windowNote`; it is no
        // longer what the drawer prints.
        /**
         * **Three readings, and the product decides which** once it has been
         * asked. Telling *Not working* from *No capacity* is exactly what this
         * file's own search got wrong on 2026-09-23 — it counted the demo's
         * reservations as a full day — so when `scheduleProvidersList` has
         * spoken, a clinician with no shift near this patient inside the window
         * is not working and anyone else the scheduler declined to offer has no
         * room. The words are unchanged (Pete, 2026-09-22): three and no others.
         */
        const seen = providerId === undefined ? undefined : inputs.providerEligibility?.[providerId];
        const exclusion = seen
          ? !seen.hasNearbyShifts || !seen.hasNonOverflowShifts
            ? EXCLUSION.notWorking
            : EXCLUSION.noCapacity
          : asked
            ? EXCLUSION.noCapacity
            : reason === "notWorking"
              ? EXCLUSION.notWorking
              : EXCLUSION.noCapacity;
        return { ...common, eligible: false, listing: "other", exclusion, windowNote };
      }
      const engineSlot = providerId !== undefined ? bySlotProvider.get(providerId) : undefined;
      // The same reading the product gives an option: the longer of the two legs.
      const drive = engineSlot ? Math.max(engineSlot.originDriveMinutes, engineSlot.destinationDriveMinutes ?? 0) : estimateDriveMinutes(primaryTerritory(entry, state.config.branch), territory);
      // The scheduler's own hour when it gave one, this file's search otherwise.
      // Neither is possible only when the caller asked the service and it named
      // a provider it then offered no slot for, which is the same no-capacity
      // the guard above reports.
      const at = engineSlot
        ? { date: toDateKey(new Date(engineSlot.startIso), tz), time: formatInBranchZone(new Date(engineSlot.startIso), tz, "HH:mm") }
        : slot;
      if (!at) return { ...common, eligible: false, listing: "other", exclusion: EXCLUSION.noCapacity };
      return {
        ...common,
        eligible: true,
        listing: "option",
        slotDate: at.date,
        slotTime: at.time,
        partOfDay: partOfDayFor(at.time),
        driveMinutes: drive,
        score: engineSlot?.totalScore,
        rankCategory: engineSlot?.rankCategory,
        windowNote,
      };
    })
    .sort((a, b) => Number(b.eligible) - Number(a.eligible) || (a.slotDate ?? "9").localeCompare(b.slotDate ?? "9") || (a.driveMinutes ?? 99) - (b.driveMinutes ?? 99) || a.displayName.localeCompare(b.displayName));
  const eligible = candidates.filter((c) => c.eligible);
  const socOptions = candidates.filter((c) => c.listing === "option");
  const otherClinicians = candidates.filter((c) => c.listing === "other").sort((a, b) => a.displayName.localeCompare(b.displayName));
  /**
   * **Unavailable SOC Clinicians**, alphabetical: the SOC-capable clinicians who
   * could have taken this and cannot. It used to carry the options whose window
   * had a day they could not take as well — but those clinicians are available,
   * they are on the list above with an hour against their name, and a heading
   * that calls them unavailable is simply wrong (Pete, 2026-09-22).
   */
  const disclosure = candidates.filter((c) => c.listing === "other").sort((a, b) => a.displayName.localeCompare(b.displayName));
  /**
   * How many and by when. **Not who** (Pete, 2026-09-22): the roll-call of
   * clinicians and hours that used to follow an em dash here is SOC Options,
   * named and pickable, a few inches down the same panel.
   *
   * **And the same number the table below is showing.** This row used to count
   * the demo's own SOC lane, which knows nothing about the product's scheduler,
   * so once the scheduler became the source of the options the card read *0
   * SOC-capable slot* above a table listing one (Pete, 2026-09-24). A check that
   * contradicts the thing it is checking is worse than no check. When the
   * scheduler has answered, the count is what it offered; the lane is still the
   * reading when nobody asked it.
   */
  const slotsOffered = asked ? socOptions.length : Math.min(lane.open, Math.max(eligible.length, 1));
  checks.push({
    key: "socWithin48h",
    label: "SOC inside 48h",
    status: pending ? "info" : socOptions.length > 0 && (asked || lane.open > 0) ? "pass" : eligible.length > 0 ? "warn" : "fail",
    detail: pending
      ? "Loading…"
      : socOptions.length
        ? `${slotsOffered} SOC-capable slot${slotsOffered === 1 ? "" : "s"} before ${socByDay(socDue, tz)}.`
        : `No SOC-capable clinician with the required traits is working before ${socByDay(socDue, tz)}.`,
  });

  // 3. Capacity, one line per ordered discipline, tightest first. Next week's
  //    committed load with this referral's expected plan added, against the target.
  const thisWeek = weekStartKey(today);
  const nextWeek = addDaysToKey(thisWeek, 7);
  // An accepted referral's hold is already in the read, so there is nothing
  // prospective left to add and the two figures collapse to one.
  const preview = referral.status === "accepted" ? undefined : prospectiveHold(state, referral, inputs.wallNow);
  const capacity = territory ? capacityRowsFor(state, referral, plan, territory, nextWeek, preview) : [];
  checks.push({
    key: "capacity",
    label: "Capacity",
    status: capacity.some((r) => r.over) ? "warn" : "pass",
    detail: capacity.length
      ? `Next week with this patient: ${capacity.map((r) => `${DISCIPLINE_GROUP_SHORT[r.group] ?? r.group} ${r.withThis} of ${r.target}`).join(", ")}`
      : "No territory for this address.",
  });

  // 4. The expected plan in points, one row per discipline.
  const tail = buildTail(orders, state.config, plan.horizonWeeks);
  const expectedPlan: ExpectedPlanRow[] = tail.map((t) => {
    const order = orders.find((o) => o.discipline === t.discipline)!;
    return { discipline: t.discipline, requiredTraits: t.requiredTraits, pattern: order.pattern, patternWords: describePlanPattern(order.pattern), pointsPerWeek: t.pointsPerWeek };
  });

  // **No authorization row.** Every check in this card is something we computed
  // from the schedule; authorization is a fact intake obtains and the EMR sends
  // us, and a row under a heading that says Checks claims we ran it (script
  // author, discussion turn 64). The payer is a fact and stays in the panel
  // header. Authorization keeps its one honest place, as a gate on a reservation:
  // the hold converts when the status lands, the pending-admissions row reads
  // "welcome call done · awaiting SOC auth" until it does, and the worklist
  // carries the due date.

  const best = socOptions[0];
  const socSlot: SocSlotAnswer | undefined = best?.slotDate && best.slotTime ? { handle: best.handle, displayName: best.displayName, date: best.slotDate, windowStart: best.slotTime, windowEnd: holdWindowEnd(best.slotTime, state.config), partOfDay: partOfDayFor(best.slotTime) } : undefined;

  const fails = checks.filter((c) => c.status === "fail");
  const warns = checks.filter((c) => c.status === "warn" || c.status === "caution");
  const summary: CheckSummary = fails.length ? "blocked" : warns.length ? "marginal" : "clear";
  return { ...base, answers: { socSlot }, checks, capacity, summary, candidates, socOptions, otherClinicians, disclosure, expectedPlan };
}

/**
 * The slot a hold takes, for Accept and for the re-hold after a discharge date
 * changes. One function so the two never disagree: the first open slot on a
 * SOC-capable clinician who carries the required traits, inside the window.
 */
export function holdSlotFor(state: DemoState, referral: Referral, opts: { dueWindow?: { start: DateKey; end: DateKey }; wallNow?: Date } = {}): HoldSlot | undefined {
  const { dueWindow } = opts;
  const tz = state.config.branch.timeZone;
  const today = toDateKey(selectDemoNow(state, opts.wallNow ?? new Date()), tz);
  const days = holdWindowDays(referral, today, dueWindow);
  if (!days.length) return undefined;
  const patient = state.patients[referral.patientRef];
  // The same disciplines and traits the panel reads, so the slot the button
  // names and the slot Accept takes can never disagree.
  const planOrders = expectedPlanFor(state.config, referral).orders;
  const socPath = socPathDisciplines((referral.orders.length ? referral.orders : planOrders).map((o) => o.discipline));
  const socOrder = planOrders.find((o) => socPath.includes(o.discipline as never)) ?? referral.orders.find((o) => socPath.includes(o.discipline as never)) ?? referral.orders[0];
  const requiredTraits = Array.from(new Set([...state.config.visitTypes.SOC.requiredTraits, ...(socOrder?.requiredTraits ?? [])]));
  const territory = territoryForZip(state.config.branch, patient?.zip);
  const eligible = rosterEntries(state)
    .filter((e) => e.roleKind === "assessing" && socPath.includes(e.discipline as never) && !isInRamp(e, today) && isSocCapable(e, today) && hasTraits(e, requiredTraits))
    .map((entry) => ({ entry, slot: firstOpenSlot(state, entry, days, { excludeHoldIds: ownHoldIds(state, referral.id) }).slot, drive: estimateDriveMinutes(primaryTerritory(entry, state.config.branch), territory) }))
    .filter((c): c is { entry: RosterEntry; slot: { date: DateKey; time: string }; drive: number } => !!c.slot)
    .sort((a, b) => a.slot.date.localeCompare(b.slot.date) || a.drive - b.drive || a.entry.displayName.localeCompare(b.entry.displayName));
  const best = eligible[0];
  if (!best) return undefined;
  return { providerHandle: best.entry.handle, date: best.slot.date, windowStart: best.slot.time, windowEnd: holdWindowEnd(best.slot.time, state.config) };
}

/**
 * **One label, whatever is picked** (Pete, 2026-09-22). The button used to name
 * the day, the part of day and the clinician it would hold — which was the only
 * way to know what Accept would do while the SOC options were a read-only table.
 * They are pickable now, so the row carries the answer and the button says what
 * it does.
 */
export const ACCEPT_LABEL = "Accept and reserve SOC slot";

/**
 * The one button, naming what it will hold: "Accept and hold Thursday morning on
 * Dana Reyes", or "Accept and hold Thursday, unassigned" when nobody fits. It
 * reads the same slot search Accept itself uses, so the label can never promise
 * a clinician the hold does not land on.
 */
export function acceptButtonLabel(state: DemoState, referralId: string, wallNow?: Date): string {
  const referral = state.referrals[referralId];
  if (!referral) return ACCEPT_LABEL;
  const tz = state.config.branch.timeZone;
  const slot = holdSlotFor(state, referral, { wallNow }) ?? unassignedFallbackSlot(state, referral);
  const day = formatInBranchZone(dateKeyStart(slot.date, tz), tz, "EEEE");
  const who = slot.providerHandle ? state.roster[slot.providerHandle]?.displayName : undefined;
  return ACCEPT_LABEL;
}

/** The one-line receipt the queue shows after Accept. */
export function acceptReceiptText(state: DemoState, referralId: string, placed?: Hold): string {
  const referral = state.referrals[referralId];
  const hold = placed ?? Object.values(state.holds).find((h) => h.referralId === referralId && h.status === "held");
  const tz = state.config.branch.timeZone;
  const name = referral ? state.patients[referral.patientRef]?.displayName ?? "The patient" : "The patient";
  if (!hold) return `${name} is on Pending admissions.`;
  const who = hold.providerHandle ? state.roster[hold.providerHandle]?.displayName : undefined;
  const day = formatInBranchZone(dateKeyStart(hold.date, tz), tz, "EEEE MMM d");
  return `${name} accepted — ${day} ${partOfDayFor(hold.windowStart)}${who ? ` held on ${who}` : ", unassigned"}.`;
}

/** Points one SOC hold occupies — the provisional layer on the branch read. */
export function holdPoints(state: Pick<DemoState, "config">): number {
  return pointsFor(state.config, "SOC");
}
